import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Button } from '../../atoms/Button';
import { cn } from '../../utils/cn';
import type { AnimatedButtonState, AnimatedSubmitButtonProps } from './types';

// Singleton pattern: track if styles have been injected globally
let stylesInjected = false;

/** @internal Reset styles injection state (for testing only) */
export function _resetStylesInjected() {
  stylesInjected = false;
}

const ANIMATION_STYLES = `
  @keyframes asb-fadeOut {
    from { opacity: 1; }
    to { opacity: 0; }
  }
  @keyframes asb-colorWipe {
    from { transform: scaleY(0); }
    to { transform: scaleY(1); }
  }
  @keyframes asb-slideInDown {
    from { transform: translateY(-100%); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
  @keyframes asb-slideOutDown {
    from { transform: translateY(0); opacity: 1; }
    to { transform: translateY(100%); opacity: 0; }
  }
  @keyframes asb-drawCheckmark {
    from { stroke-dashoffset: 35; }
    to { stroke-dashoffset: 0; }
  }
  @keyframes asb-spin {
    100% { transform: rotate(360deg); }
  }
  .asb-animate-fade-out {
    animation: asb-fadeOut 0.5s ease-out forwards;
  }
  .asb-animate-color-wipe {
    animation: asb-colorWipe 0.8s cubic-bezier(0.1, 0.5, 0.1, 1) forwards;
    transform-origin: top;
  }
  .asb-animate-slide-in-down {
    animation: asb-slideInDown 0.5s ease-out forwards;
  }
  .asb-animate-slide-in-down-delayed {
    animation: asb-slideInDown 0.8s cubic-bezier(0.1, 0.5, 0.1, 1) forwards;
  }
  .asb-animate-slide-out-down {
    animation: asb-slideOutDown 0.8s cubic-bezier(0.1, 0.5, 0.1, 1) forwards;
  }
  .asb-animate-draw-checkmark {
    animation: asb-drawCheckmark 0.8s ease-out forwards;
  }
  .asb-spinner {
    transform-origin: center;
    animation: asb-spin 0.9s infinite linear;
  }
`;

/**
 * Injects animation styles into the document head once globally.
 * Uses singleton pattern to avoid duplicate style tags.
 */
function useAnimationStyles() {
  useEffect(() => {
    if (stylesInjected || typeof document === 'undefined') return;

    const styleElement = document.createElement('style');
    styleElement.setAttribute('data-asb-styles', '');
    styleElement.textContent = ANIMATION_STYLES;
    document.head.appendChild(styleElement);
    stylesInjected = true;
  }, []);
}

/**
 * Animated submit button with multiple states and smooth transitions
 *
 * Success flow timeline:
 * 1. Idle - Initial text (e.g., "Save changes")
 * 2. Loading - Spinner (while HTTP request pending)
 * 3. Spinner fade-out (500ms)
 * 4. Color wipe green from top (800ms) + pause (0ms)
 * 5. Checkmark draws (800ms) + pause (200ms)
 * 6. Checkmark slides out, success text slides in (500ms each, sequential) + pause (1000ms)
 * 7. onSuccess callback fires
 */
export function AnimatedSubmitButton({
  isPending,
  isSuccess,
  isError,
  initialText,
  loadingText: _loadingText,
  successText,
  errorText,
  onSuccess,
  disabled,
  className = '',
  onClick,
  ...buttonProps
}: AnimatedSubmitButtonProps) {
  // Inject animation styles once globally
  useAnimationStyles();

  const [buttonState, setButtonState] = useState<AnimatedButtonState>({
    status: 'idle',
    text: initialText,
  });

  // Sync buttonState.text with initialText when idle
  useEffect(() => {
    if (buttonState.status === 'idle') {
      const currentText = buttonState.text;
      if (currentText !== initialText) {
        setButtonState({ status: 'idle', text: initialText });
      }
    }
  }, [initialText, buttonState]);

  // Track if we've started the success animation to prevent re-triggering
  const animationStartedRef = useRef(false);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  // Animation timing constants (in ms)
  const SPINNER_FADE_OUT = 500;
  const COLOR_WIPE = 800;
  const PAUSE_AFTER_COLOR = 0;
  const CHECKMARK_DRAW = 800;
  const PAUSE_AFTER_CHECKMARK = 200;
  const TEXT_SLIDE = 1000; // 500ms checkmark slide-out + 500ms text slide-in
  const PAUSE_AFTER_TEXT = 1000;

  // Handle all state transitions based on props
  useEffect(() => {
    // Reset animation flag when going back to idle
    if (!isPending && !isSuccess && !isError) {
      animationStartedRef.current = false;
    }

    // Transition to loading state
    if (isPending && buttonState.status === 'idle') {
      animationStartedRef.current = false;
      setButtonState({ status: 'loading' });
      return;
    }

    // Transition to success flow - only trigger once
    if (!isPending && isSuccess && !animationStartedRef.current) {
      animationStartedRef.current = true;
      // Clear any existing timers
      timersRef.current.forEach(timer => clearTimeout(timer));
      timersRef.current = [];

      // Step 3: Spinner fades out
      setButtonState({ status: 'spinner-fade-out' });

      let elapsed = SPINNER_FADE_OUT;

      // Step 4: Color wipe (starts after spinner fade)
      const timer1 = setTimeout(() => {
        setButtonState({ status: 'color-transition' });
      }, elapsed);

      elapsed += COLOR_WIPE + PAUSE_AFTER_COLOR;

      // Step 5: Checkmark draws
      const timer2 = setTimeout(() => {
        setButtonState({ status: 'checkmark' });
      }, elapsed);

      elapsed += CHECKMARK_DRAW + PAUSE_AFTER_CHECKMARK;

      // Step 6: Success text slides in
      const timer3 = setTimeout(() => {
        setButtonState({ status: 'success', message: successText });
      }, elapsed);

      elapsed += TEXT_SLIDE + PAUSE_AFTER_TEXT;

      // Step 7: Callback fires
      const timer4 = setTimeout(() => {
        onSuccess?.();
      }, elapsed);

      timersRef.current = [timer1, timer2, timer3, timer4];
      return;
    }

    // Transition to error state
    if (!isPending && isError && buttonState.status === 'loading') {
      setButtonState({ status: 'error', message: errorText });
      return;
    }
  }, [isPending, isSuccess, isError, buttonState.status, successText, errorText, onSuccess]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      timersRef.current.forEach(timer => clearTimeout(timer));
      timersRef.current = [];
    };
  }, []);

  // Reset to idle when user clicks after error, and compose with parent onClick
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(event);
      if (buttonState.status === 'error') {
        setButtonState({ status: 'idle', text: initialText });
      }
    },
    [buttonState.status, initialText, onClick]
  );

  // Determine button background color based on state
  // Note: color-transition state keeps red background - the green wipe is done via overlay
  const getBackgroundColor = () => {
    switch (buttonState.status) {
      case 'checkmark':
      case 'success':
        return '!bg-velux-splash-green'; // Green color (! for important to override variant)
      case 'error':
        return '!bg-velux-red'; // Red color (error stays red)
      default:
        return ''; // Use default from variant prop (red)
    }
  };

  // Determine if button should be disabled
  const isDisabled =
    disabled ||
    buttonState.status === 'loading' ||
    buttonState.status === 'spinner-fade-out' ||
    buttonState.status === 'color-transition' ||
    buttonState.status === 'checkmark' ||
    buttonState.status === 'success';

  return (
    <Button
      {...buttonProps}
      disabled={isDisabled}
      className={cn(
        'relative overflow-hidden disabled:!opacity-100',
        getBackgroundColor(),
        className
      )}
      onClick={handleClick}
    >
      {/* Green background overlay - wipes in during color-transition, stays visible for checkmark/success */}
      {(buttonState.status === 'color-transition' ||
        buttonState.status === 'checkmark' ||
        buttonState.status === 'success') && (
        <div
          className={cn(
            'absolute inset-0 !bg-velux-splash-green z-0',
            buttonState.status === 'color-transition' && 'asb-animate-color-wipe'
          )}
        />
      )}

      {/* Visually hidden live region for screen reader announcements */}
      <span className="sr-only" aria-live="polite" aria-atomic="true">
        {buttonState.status === 'loading' && 'Loading'}
        {buttonState.status === 'success' && buttonState.message}
        {buttonState.status === 'error' && buttonState.message}
      </span>

      {/* Button content */}
      <div className="relative z-10 flex items-center justify-center gap-2 min-h-[32px] overflow-hidden">
        {/* Idle state */}
        {buttonState.status === 'idle' && <span>{buttonState.text}</span>}

        {/* Loading state - spinner spinning */}
        {buttonState.status === 'loading' && <SpinnerIcon className="w-8 h-8" />}

        {/* Spinner fade-out state */}
        {buttonState.status === 'spinner-fade-out' && (
          <SpinnerIcon className="asb-animate-fade-out w-8 h-8" />
        )}

        {/* Color transition state - empty (just showing green wipe) */}
        {buttonState.status === 'color-transition' && <div className="w-8 h-8" />}

        {/* Checkmark - renders in both checkmark and success states to avoid flicker */}
        {(buttonState.status === 'checkmark' || buttonState.status === 'success') && (
          <CheckmarkIcon
            className={cn(
              'w-6 h-6',
              buttonState.status === 'success' && 'absolute asb-animate-slide-out-down'
            )}
          />
        )}

        {/* Success state text sliding in */}
        {buttonState.status === 'success' && (
          <span className="asb-animate-slide-in-down-delayed">{buttonState.message}</span>
        )}

        {/* Error state */}
        {buttonState.status === 'error' && (
          <div className="flex items-center gap-2 asb-animate-slide-in-down">
            <AlertErrorIcon className="w-4 h-4" />
            <span>{buttonState.message}</span>
          </div>
        )}
      </div>
    </Button>
  );
}

/**
 * Animated spinner icon (arc style)
 */
function SpinnerIcon({ className = '' }: { className?: string }) {
  return (
    <svg
      className={className}
      width="32"
      height="32"
      viewBox="0 0 32 32"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle
        cx="16"
        cy="16"
        r="14"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="square"
        strokeDasharray="22 66"
        className="asb-spinner"
      />
    </svg>
  );
}

/**
 * Animated checkmark icon with stroke drawing effect
 * Uses validation-check-bold-24.svg path
 */
function CheckmarkIcon({ className = '' }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M5.5 12.4423C7.25972 13.6468 10.3745 17.2039 11.3532 19.5C12.84 14.3996 16.6982 7.0596 18.6744 4.5"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="square"
        className="asb-animate-draw-checkmark"
        style={{
          strokeDasharray: 35,
          strokeDashoffset: 35,
        }}
      />
    </svg>
  );
}

/**
 * Alert/error icon for error state
 */
function AlertErrorIcon({ className = '' }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M8.00008 15.3337C12.0502 15.3337 15.3334 12.0504 15.3334 8.00033C15.3334 3.95024 12.0502 0.666992 8.00008 0.666992C3.94999 0.666992 0.666748 3.95024 0.666748 8.00033C0.666748 12.0504 3.94999 15.3337 8.00008 15.3337Z"
        stroke="currentColor"
      />
      <path d="M4.33325 4.3335L11.8757 11.876" stroke="currentColor" strokeMiterlimit="10" />
    </svg>
  );
}

AnimatedSubmitButton.displayName = 'AnimatedSubmitButton';
